import React from 'react'
import { Button as HeadlessButton } from '@headlessui/react'
import clsx from 'clsx'

export enum Type {
  DEFAULT = 'button',
  SUBMIT = 'submit',
}

export interface IButton {
  text?: string | React.ReactNode
  type?: string
  href?: string
  onClick?: () => void
  className?: string
  disabled?: boolean
  small?: boolean
  invert?: boolean
  contrast?: boolean
  newTab?: boolean
}

const base =
  'inline-flex items-center justify-center rounded-lg border font-medium cursor-pointer transition-colors ' +
  'focus:outline-none focus:ring-0 focus:ring-offset-0 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed ' +
  'data-[focus]:outline-none data-[focus]:ring-0 data-[focus]:ring-offset-0'

const solidLight =
  'border-gray-900 bg-gray-900 text-white hover:bg-gray-800 dark:border-gray-600 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500'
const outlineLight =
  'border-gray-300 bg-transparent text-gray-900 hover:bg-gray-100 dark:border-gray-500 dark:text-white dark:hover:bg-white/10'
const invertLight =
  'border-white bg-white text-gray-900 hover:bg-gray-100 dark:border-gray-400 dark:bg-gray-400 dark:text-gray-900 dark:hover:bg-gray-300'

const sizeDefault = 'px-4 py-2 text-sm'
const sizeSmall = 'px-2 py-1 text-sm sm:px-3'

const Button: React.FC<IButton> = ({
  text,
  onClick,
  href,
  small = false,
  type = Type.DEFAULT,
  className = '',
  disabled = false,
  invert = false,
  contrast = false,
  newTab = false,
}) => {
  const variant = invert ? invertLight : contrast ? outlineLight : solidLight
  const size = small ? sizeSmall : sizeDefault
  const classes = clsx(base, variant, size, className)
  const content = <>{text}</>

  if (href) {
    return (
      <HeadlessButton
        as="a"
        href={href}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
        disabled={disabled}
        className={classes}
      >
        {content}
      </HeadlessButton>
    )
  }

  return (
    <HeadlessButton
      type={type === Type.SUBMIT ? 'submit' : 'button'}
      onClick={type !== Type.SUBMIT ? onClick : undefined}
      disabled={disabled}
      className={classes}
    >
      {content}
    </HeadlessButton>
  )
}

export default Button
